import {
	type OrderDetailResponseDto,
	type OrderPriceResponseDto,
	type OrderSetupDetailResponseDto,
	OrderStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { OrderComments } from "Order/Components/Comments/OrderComments";
import { B2BPromoForm } from "Order/Components/Form/B2BPromoForm";
import { useCustomerEditForm } from "Order/Components/Form/Hooks/useCustomerEditForm";
import { useOrderEditForm } from "Order/Components/Form/Hooks/useOrderEditForm";
import { UpdateZoneDialog } from "Order/Components/UpdateZoneDialog";
import { OrderPriceEditWidget } from "Order/Modules/Price/Widgets/OrderPriceEditWidget";
import { getTransportZone } from "Order/Transport/transport";
import { AssignPartnerCardWidget } from "Order/Widgets/AssignPartnerCardWidget";
import { CalculatorCardWidget } from "Order/Widgets/CalculatorCardWidget";
import { CustomerEditWidget } from "Order/Widgets/CustomerEditWidget";
import { DateNextActionCardWidget } from "Order/Widgets/DateNextActionCardWidget";
import { GeneralEditCardWidget } from "Order/Widgets/GeneralEditCardWidget";
import { OrderAssignUserWidget } from "Order/Widgets/OrderAssignUserWidget";
import { PackageTypeCardWidget } from "Order/Widgets/PackageTypeCardWidget";
import { StatusCardWidget } from "Order/Widgets/StatusCardWidget";
import { ExpandCollapseButton } from "Shared/Components/Animation/ExpandCollapseAnimation";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { CLAIM_STATUS_ACTIVE } from "Shared/consts";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Fab, Grid2 } from "@mui/material";
import { useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { toast } from "sonner";

export type OrderEditTabProps = {
	order: OrderDetailResponseDto;
	orderSetup?: OrderSetupDetailResponseDto;
	orderPrice?: OrderPriceResponseDto;
};

const orderResources = Resources.base.order.editForm;

export function OrderEditTab({
	order,
	orderSetup,
	orderPrice,
}: OrderEditTabProps) {
	const { t } = useResource();
	const [showZoneDialog, setShowZoneDialog] = useState(false);
	const [isZoneFetching, setIsZoneFetching] = useState(false);
	const pendingAddressRef = useRef<string>("");

	const isActiveClaim = order.claims.some(
		(claim) => claim.status === CLAIM_STATUS_ACTIVE,
	);
	const {
		form: orderForm,
		handleSubmitUpdate: handleSubmitOrder,
		isPending,
		statusValue,
	} = useOrderEditForm(order, t, orderSetup);
	const isStatusChangeBlocked =
		isActiveClaim && statusValue === OrderStatus.done;

	const {
		form: customerForm,
		isPending: isPendingCustomer,
		handleSubmitUpdate: handleSubmitCustomer,
	} = useCustomerEditForm(order.customer, order.id);

	const transportField = order.fields.find(
		(f) => f.orderField.key === "transport",
	);
	const canShowZoneDialog = !!(order.region?.regionCode && transportField);

	const saveForm = () => {
		orderForm.formState.isDirty && handleSubmitOrder();
		customerForm.formState.isDirty && handleSubmitCustomer();
	};

	const handleSubmit = () => {
		if (canShowZoneDialog && orderForm.formState.dirtyFields.address) {
			pendingAddressRef.current = orderForm.getValues().address ?? "";
			setShowZoneDialog(true);
		} else {
			saveForm();
		}
	};

	const handleSaveWithoutZone = () => {
		setShowZoneDialog(false);
		saveForm();
	};

	const handleSaveWithZone = async () => {
		setIsZoneFetching(true);
		try {
			const { zone } = await getTransportZone(
				pendingAddressRef.current,
				order.region!.regionCode!,
			);
			const fields = orderForm.getValues().fields ?? [];
			const transportIdx = fields.findIndex((f) => f.key === "transport");
			if (transportIdx !== -1) {
				orderForm.setValue(`fields.${transportIdx}.value`, `zone_${zone}`, {
					shouldDirty: true,
				});
			}
			setShowZoneDialog(false);
			saveForm();
		} catch {
			toast.error("Failed to fetch transport zone");
		} finally {
			setIsZoneFetching(false);
		}
	};

	const isMaterialPrice = ["flooring", "renovations"].includes(
		order?.vertical?.name ?? "",
	);

	return (
		<>
			<Grid2
				container
				spacing={2}
				alignItems="flex-start"
				direction={{ md: "row" }}
			>
				{orderPrice && (
					<>
						<Grid2 size={{ xs: 8 }}>
							<BaseCard title={t(orderResources.price)}>
								<OrderPriceEditWidget
									orderPrice={orderPrice}
									orderId={order.id}
									isMaterialPrice={isMaterialPrice}
									isDisabled={order.sfInvoicePeriod !== null}
								/>
							</BaseCard>
						</Grid2>
						<Grid2 size={{ xs: 4 }}>
							<CalculatorCardWidget order={order} />
						</Grid2>
					</>
				)}
				<Grid2 container alignItems="flex-start">
					<Grid2 size={{ xs: 12 }}>
						<BaseCard
							title={t(orderResources.title)}
							sxCardContent={{
								p: 1,
							}}
						>
							<FormProvider {...orderForm}>
								<Grid2 container spacing={2}>
									<Grid2 container size={{ xs: 12, md: 8 }} spacing={2}>
										<GeneralEditCardWidget
											order={order}
											orderSetup={orderSetup}
										/>
										<Grid2 size={{ xs: 12 }}>
											<CustomerEditWidget form={customerForm} />
										</Grid2>
									</Grid2>

									<Grid2
										container
										size={{ xs: 12, md: 4 }}
										flexDirection="column"
										justifyContent="start"
										spacing={2}
									>
										<Grid2 size={{ xs: 12 }}>
											<StatusCardWidget
												status={statusValue}
												storedStatus={order.status}
												isDisabled={order.sfInvoicePeriod !== null}
												order={order}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12 }}>
											<PackageTypeCardWidget
												isDisabled={order.sfInvoicePeriod !== null}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12 }}>
											<DateNextActionCardWidget />
										</Grid2>
										<Grid2 size={{ xs: 12 }}>
											<AssignPartnerCardWidget order={order} />
										</Grid2>
										<Grid2 size={{ xs: 12 }}>
											<OrderAssignUserWidget order={order} />
										</Grid2>
										<Grid2 size={{ xs: 12 }}>
											<B2BPromoForm order={order} />
										</Grid2>
									</Grid2>
								</Grid2>
								<Box mt={2}>
									<Button
										variant="contained"
										loading={isPending}
										onClick={handleSubmit}
										disabled={
											!(
												customerForm.formState.isDirty ||
												orderForm.formState.isDirty
											) || isStatusChangeBlocked
										}
									>
										{t(orderResources.save)}
									</Button>
								</Box>
							</FormProvider>
						</BaseCard>
					</Grid2>
				</Grid2>
			</Grid2>

			<Box mt={2}>
				<OrderComments id={order.id} title={t(orderResources.comments)} />
			</Box>

			<ExpandCollapseButton
				showElement={
					customerForm.formState.isDirty || orderForm.formState.isDirty
				}
			>
				<Fab
					component={Button}
					color="primary"
					loading={isPending}
					onClick={handleSubmit}
					disabled={isStatusChangeBlocked}
				>
					{!(isPending || isPendingCustomer) && <SaveIcon fontSize="large" />}
				</Fab>
			</ExpandCollapseButton>

			<UpdateZoneDialog
				open={showZoneDialog}
				onClose={handleSaveWithoutZone}
				onConfirm={handleSaveWithZone}
				isPending={isZoneFetching}
			/>
		</>
	);
}
